import { ReportColumn, ReportColumnType, SortOrder } from '@ardoq/api-types';
import { isColumnSortAble, isReferenceColumn } from './utils';
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  Icon,
  IconName,
  IconSize,
} from '@ardoq/icons';
import React, { Fragment, ReactNode } from 'react';
import { ButtonType, IconButton } from '@ardoq/button';
import { PopoverContent, TextOverflow, WithPopover } from '@ardoq/popovers';
import styled from 'styled-components';
import { ReportSort } from './types';
import { FlexBox, Stack } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { colors } from '@ardoq/design-tokens';
import { CAPTURE_KEEP } from '@ardoq/export/captureMode';

type GetClickHandler = (parameters: {
  column: ReportColumn;
  sort: ReportSort;
  setSort: (sort: ReportSort) => void;
}) => VoidFunction;

const getOnSortClick: GetClickHandler =
  ({ column, sort, setSort }) =>
  () => {
    if (sort.columnKey === column.key && sort.order === SortOrder.ASC) {
      setSort({
        columnKey: column.key,
        order: SortOrder.DESC,
      });
    } else if (sort.columnKey === column.key && sort.order === SortOrder.DESC) {
      setSort({
        columnKey: null,
        order: SortOrder.DEFAULT,
      });
    } else {
      setSort({
        columnKey: column.key,
        order: SortOrder.ASC,
      });
    }
  };

const ConfidentialWarningIcon = () => (
  <Icon
    iconName={IconName.CONTROL}
    color={colors.iconWarning}
    size={IconSize.SMALL}
  />
);

type GetHeaderRender = (parameters: {
  isInteractive: boolean;
  sort?: ReportSort;
  setSort?: (sort: ReportSort) => void;
  column: ReportColumn;
  trackSorting: VoidFunction;
  withPopover?: boolean;
  indicateRenamedColumn?: boolean;
  showConfidentialWarningIcon?: boolean;
  columnDescription: string | null;
  isRightAligned?: boolean;
}) => () => ReactNode;

const ColumnName = styled.div`
  overflow: hidden;
`;

const getHeaderRender: GetHeaderRender = ({
  isInteractive,
  sort,
  column,
  setSort,
  trackSorting,
  withPopover,
  indicateRenamedColumn,
  showConfidentialWarningIcon,
  isRightAligned,
  columnDescription,
}) => {
  const { key: columnKey, label, originalLabel } = column;
  const isRenamedColumn = Boolean(originalLabel);
  const isSortable = isColumnSortAble(column);
  const onSortClick =
    isInteractive && isSortable && sort && setSort
      ? getOnSortClick({
          column,
          sort,
          setSort,
        })
      : undefined;

  const getColumnNameComponent = () => {
    const referenceColumnIcon = isReferenceColumn(column) ? (
      column.type === ReportColumnType.REFERENCE_TYPE_INCOMING ? (
        <ArrowBackIcon iconSize={IconSize.SMALL} />
      ) : (
        <ArrowForwardIcon iconSize={IconSize.SMALL} />
      )
    ) : null;

    const TextWrapper = withPopover ? TextOverflow : Fragment;

    return (
      <WithPopover
        content={
          columnDescription
            ? () => (
                <PopoverContent>
                  <Stack gap="xsmall">
                    <FlexBox align="center" gap="xsmall">
                      <Text variant="text1Bold">{label}</Text>
                      {indicateRenamedColumn && isRenamedColumn && (
                        <Text variant="text2" color="textSubtle">
                          (renamed)
                        </Text>
                      )}
                    </FlexBox>
                    <Text variant="text2">{columnDescription}</Text>
                  </Stack>
                </PopoverContent>
              )
            : null
        }
      >
        <ColumnName
          style={{
            ...(isInteractive && isSortable ? { cursor: 'pointer' } : {}),
            ...(isRightAligned ? { marginLeft: 'auto' } : {}),
          }}
          onClick={() => {
            if (onSortClick) {
              onSortClick();
              trackSorting();
            }
          }}
        >
          <FlexBox align="center" gap="xsmall">
            <TextWrapper>{label}</TextWrapper>
            {showConfidentialWarningIcon && <ConfidentialWarningIcon />}
            {!isRenamedColumn && referenceColumnIcon}
            {indicateRenamedColumn && isRenamedColumn && (
              <Text variant="text2" color="textSubtle">
                (renamed)
              </Text>
            )}
          </FlexBox>
        </ColumnName>
      </WithPopover>
    );
  };

  return () => (
    <>
      {getColumnNameComponent()}
      {sort?.columnKey === columnKey && (
        <IconButton
          label={`Sort by ${label} ${sort.order === SortOrder.ASC ? 'descending' : 'ascending'}`}
          type="button"
          iconName={
            sort.order === SortOrder.ASC
              ? IconName.ARROW_UPWARD
              : IconName.ARROW_DOWNWARD
          }
          isDisabled={!isInteractive}
          buttonType={ButtonType.GHOST}
          data-capture={CAPTURE_KEEP}
          onClick={() => {
            if (onSortClick) {
              onSortClick();
              trackSorting();
            }
          }}
        />
      )}
    </>
  );
};

export { getHeaderRender };
